import React from 'react';
import { DetailCardType } from 'types/DetailPageTypes';
import { FieldType } from 'types/FieldTypes';
import { TabType } from 'types/TabTypes';
import DetailPage from 'components/layout/DetailPage';
import { HeaderButtons } from 'types/ButtonTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useActiveFeature } from 'services/permissions/permissionChecks';

// Rename the name of the function:
const ProductDetail: React.FC = () => {

    // Specify the parameters of this detail page:
    const apiObject = 'product'
    const pageObject = 'product'
    const dimensionPricingActive = useActiveFeature('dimension_pricing');

    // Define the header title fields:
    const headerTitleFields: FieldType[] = [
        {
            name: 'name',
            type: 'text',
            placeholder: 'product.general.name_placeholder',
            saveOnBlur: true,
        },
    ]

    // Define the header buttons:
    const headerButtons: HeaderButtons = [
        {   
            type: 'icon',
            icon: <FontAwesomeIcon icon={faTrash} />,
            tooltipText: 'general.deactivate',
            action: 'deactivate',
            onSuccess: 'redirect',
            redirectTo: '/products',
            size: 'medium'
        },
    ]

    // Define the detail page tabs:
    const tabs: TabType[] = [ 
        { label: 'timeline.general.timeline_label', type: 'timeline', showOnlyTimelineUpdates: true },
        { label: 'product.general.extended_description_tab_label', type: 'product-description' },
        ...(dimensionPricingActive ? [{
            label: 'product.general.price_matrix_tab_label',
            type: 'product-price-matrix',
            condition: [{ field: 'pricing_method_effective', values: ['dimensions'] }],
        }] : []),
    ]

    // Define detail components and add fields to FieldsCards:
    const detailCards: DetailCardType[] = [
        {
            component: 'FieldsCard',
            title: 'product.general.detail_card_label',
            alignment: 'horizontal',
            fields: [
                { 
                    type: 'text', 
                    name: 'number', 
                    label: 'product.general.number_label', 
                    placeholder: 'product.general.number_placeholder',
                },
                { 
                    type: 'dropdown', 
                    name: 'category', 
                    label: 'product.general.category_label', 
                    apiRequest: [{
                        endpoint: 'get_productcategory_list',
                        object: 'productcategory',
                        params: { is_active: true, is_product: true },
                        useCache: true
                    }],
                    disabled_selected: 'product.general.category_disabled_selected',
                },
                { 
                    type: 'dropdown', 
                    name: 'pricing_method', 
                    label: 'product.general.pricing_method_label', 
                    tooltipText: 'product.general.pricing_method_tooltipText',
                    disabled_selected: 'forms.select_type_disabled_selected',
                    defaultValue: { value: 'per_unit' },
                    allowNoneOption: false,
                    showSearch: false,
                    activeFeature: 'dimension_pricing',
                    hardcodedOptions: [
                        { value: 'per_unit', name: 'product.pricing_method.per_unit' },
                        { value: 'dimensions', name: 'product.pricing_method.dimensions' },
                    ],
                },
                { 
                    type: 'text', 
                    name: 'unit', 
                    label: 'product.general.unit_label', 
                    placeholder: 'product.general.unit_placeholder', 
                },
                { 
                    type: 'price-currency', 
                    name: 'sale_price', 
                    label: 'product.general.sale_price_label', 
                    placeholder: 'product.general.sale_price_placeholder', 
                    apiObject: 'currency',
                },
                { 
                    type: 'dropdown', 
                    name: 'vat_rate', 
                    label: 'product.general.vat_rate_label', 
                    apiRequest: [{
                        endpoint: 'get_vatrate_list',
                        object: 'vatrate',
                        params: { is_active: true },
                        useCache: true
                    }],
                    disabled_selected: 'product.general.vat_rate_disabled_selected',
                },
                { 
                    type: 'dropdown', 
                    name: 'ledger_account', 
                    label: 'product.general.ledger_account_label', 
                    apiRequest: [{
                        endpoint: 'get_ledgeraccount_list',
                        object: 'ledgeraccount',
                        params: { 
                            is_active: true,
                            ledger_account_type: 'revenue'
                        },
                        useCache: true
                    }],
                    disabled_selected: 'product.general.ledger_account_disabled_selected',
                },
                { 
                    type: 'image', 
                    name: 'image', 
                    label: 'product.general.image_label', 
                    imageType: 'landscape',
                    dropzoneText: 'product.general.image_placeholder', 
                    apiField: { 
                        fileUpload: 'image_file', 
                        fileName: 'image_filename',
                        imageDeleted: 'image_deleted'
                    },
                    cropConfig: {
                        aspectRatio: 4/3,
                        cropShape: 'rect',
                        resize: { width: 400, height: 300 }
                    }
                },
                { 
                    type: 'tabs', 
                    name: 'is_inventory', 
                    label: 'product.general.inventory_item_label', 
                    tooltipText: 'product.general.inventory_item_tooltipText',
                },
                { 
                    type: 'price-currency', 
                    name: 'purchase_price', 
                    label: 'product.general.purchase_price_label', 
                    placeholder: 'product.general.purchase_price_placeholder', 
                    apiObject: 'currency',
                },
                // { 
                //     type: 'date', 
                //     name: 'active_until', 
                //     label: 'product.general.active_until_label', 
                // },
            ],
        },
        // Add more detail components if needed:
    ]

    return (
        <DetailPage
            apiObject={apiObject}
            pageObject={pageObject}
            headerTitleFields={headerTitleFields}
            headerButtons={headerButtons}
            detailCards={detailCards}
            tabs={tabs}
        />
    );
};

export default ProductDetail;